import React, { useEffect, useRef } from 'react'
import { subscribe } from 'react-contextual'
import { SearchFilterProvider } from '../../../providers'
import Filters from './Filters/Filters'
import SearchField from './SearchField/SearchField'
import styles from './SearchFilter.module.scss'
import cx from 'classnames'
import { useEffectOnce } from 'react-use'
import { StringParam, useQueryParam } from 'use-query-params'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'

type TFilter = {
  key: string
  extraKeys?: Record<string, string>
  label: string
  count: number
  onClick: () => void
  hideCount?: boolean
}

interface ISearchFilterProps {
  className?: string
  pathname: string
  redirect: (path: string) => void
  filters: TFilter[]
  subFilters?: React.ReactNode
  filter?: string
  isSearchOpen: boolean
  toggleSearchOpen: () => void
  activeFilterIndex: number
  setActiveFilterIndex: (index: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
}

function SearchFilter({
  className = '',
  pathname,
  redirect,
  filters,
  subFilters,
  // SearchFilterProvider props
  filter,
  isSearchOpen,
  toggleSearchOpen,
  activeFilterIndex,
  setActiveFilterIndex,
  searchQuery,
  setSearchQuery,
}: ISearchFilterProps): React.ReactElement {
  const searchFieldRef = useRef<HTMLInputElement | null>(null)
  const { search } = useLocation()

  const [filterParam] = useQueryParam('filter', StringParam)

  useEffectOnce(() => {
    if (!filterParam) {
      setActiveFilterIndex(0)
    }
  })

  const decoratedFilters = React.useMemo<TFilter[]>(
    () =>
      filters.map(f => {
        const extraParams = f.extraKeys
          ? Object.entries(f.extraKeys).reduce((acc, [k, v]) => `${acc}&${k}=${v}`, '')
          : ''
        return {
          ...f,
          onClick: () => {
            const preserved = { ...queryString.parse(search) }
            delete preserved.filter
            Object.keys(f.extraKeys ?? {}).forEach(key => delete preserved[key])
            const preservedParams = queryString.stringify(preserved)
            redirect(`${pathname}?filter=${f.key}${extraParams}${preservedParams ? `&${preservedParams}` : ''}`)
          },
        }
      }),
    [filters, pathname, redirect, search],
  )

  useEffect(() => {
    if (filter) {
      const index = decoratedFilters.findIndex(f => f.key === filter)
      if (index > -1) {
        setActiveFilterIndex(index)
      }
    }
  }, [filter, setActiveFilterIndex, decoratedFilters])

  useEffect(() => {
    if (!isSearchOpen) return
    const id = setTimeout(() => {
      searchFieldRef.current?.focus()
    }, 300)
    return () => clearTimeout(id)
  }, [isSearchOpen])

  const handleSetActiveFilter = (index: number, callback: any): void => {
    setActiveFilterIndex(index)
    if (typeof callback === 'function') {
      callback()
    }
  }

  const handleSearchClose = (): void => {
    toggleSearchOpen()
    setSearchQuery('')
  }

  return (
    <div className={cx([styles.root, className])}>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          {!isSearchOpen && (
            <>
              <div className={styles['expanded-filters']}>
                <Filters filters={filters} activeFilter={activeFilterIndex} setActiveFilter={handleSetActiveFilter} />
              </div>
            </>
          )}
          <SearchField
            query={searchQuery}
            isOpen={isSearchOpen}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            onOpen={toggleSearchOpen}
            onClose={handleSearchClose}
            setRef={ref => {
              searchFieldRef.current = ref
            }}
          />
        </div>
      </div>
      {subFilters}
    </div>
  )
}

type SearchFilterContext = Pick<
  ISearchFilterProps,
  | 'filter'
  | 'isSearchOpen'
  | 'toggleSearchOpen'
  | 'activeFilterIndex'
  | 'setActiveFilterIndex'
  | 'searchQuery'
  | 'setSearchQuery'
>

export default subscribe(SearchFilterProvider, (ctx: SearchFilterContext) => ctx)(SearchFilter)
